/**
 * PrivacyPolicy.tsx — Shield Privacy Policy.
 *
 * Route: /privacy — publicly accessible.
 * 13 sections with table of contents + anchor links.
 * [PLACEHOLDER] text marks areas to fill before launch.
 */

import React, { useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import SecurityIcon from '@mui/icons-material/Security';

// ── Section anchor ────────────────────────────────────────────────────────────

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <Box id={id} sx={{ mb: 5, scrollMarginTop: 80 }}>
      <Typography variant="h5" fontWeight={700} mb={2} color="primary.main">{title}</Typography>
      <Box sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{children}</Box>
    </Box>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

function UL({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      {items.map((item) => (
        <Box component="li" key={item} sx={{ mb: 0.75 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── TOC sections ──────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'who-we-are',          title: '1. Who We Are' },
  { id: 'information-collect', title: '2. Information We Collect' },
  { id: 'how-we-use',          title: '3. How We Use Your Information' },
  { id: 'legal-basis',         title: '4. Legal Basis for Processing' },
  { id: 'data-sharing',        title: '5. Data Sharing and Disclosure' },
  { id: 'data-transfers',      title: '6. International Data Transfers' },
  { id: 'data-retention',      title: '7. Data Retention' },
  { id: 'your-rights',         title: '8. Your Rights' },
  { id: 'cookies',             title: '9. Cookies and Tracking' },
  { id: 'security',            title: '10. Security Measures' },
  { id: 'children',            title: '11. Children\'s Privacy' },
  { id: 'third-party',         title: '12. Third-Party Links' },
  { id: 'changes',             title: '13. Changes to This Policy' },
  { id: 'credential-security', title: '14. Credential Security' },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, sm: 5 }, py: 2,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: 'rgba(13,17,23,0.92)', backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <SecurityIcon sx={{ color: '#00E5FF', fontSize: 26, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h6" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {user ? (
            <Button variant="contained" component={RouterLink} to="/dashboard" sx={{ color: '#FFFFFF' }}>
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button variant="outlined" component={RouterLink} to="/login" sx={{ color: '#FFFFFF', borderColor: '#FFFFFF', '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                Sign In
              </Button>
              <Button variant="contained" component={RouterLink} to="/signup" sx={{ fontWeight: 700 }}>
                Start Free Trial
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 5 }, py: { xs: 5, md: 8 } }}>

        {/* Header */}
        <Box mb={5}>
          <Typography variant="h3" fontWeight={800} mb={1.5}>Privacy Policy</Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: [LAST UPDATED DATE] · Effective: [EFFECTIVE DATE]
          </Typography>
          <Divider sx={{ mt: 3 }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>

          {/* Table of contents — sticky sidebar */}
          <Box
            component="aside"
            sx={{
              display: { xs: 'none', md: 'block' },
              width: 240,
              flexShrink: 0,
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="caption" color="text.disabled" fontWeight={700}
              sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
              Contents
            </Typography>
            <Stack spacing={0.5}>
              {SECTIONS.map(({ id, title }) => (
                <Link
                  key={id}
                  href={`#${id}`}
                  underline="none"
                  sx={{
                    fontSize: 13, color: 'text.secondary', display: 'block', py: 0.4,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {title}
                </Link>
              ))}
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            <Section id="who-we-are" title="1. Who We Are">
              <P>
                [COMPANY NAME] ("[COMPANY NAME]", "we", "us", or "our") operates Shield, a cloud security and AI
                monitoring platform accessible at [WEBSITE URL] (the "Service").
              </P>
              <P>
                We are the data controller responsible for the personal data we collect about you. Our registered
                address is [ADDRESS], [CITY], [POSTCODE], [COUNTRY]. Our data protection contact is
                privacy@[CONTACT EMAIL].
              </P>
            </Section>

            <Section id="information-collect" title="2. Information We Collect">
              <P><strong style={{ color: 'inherit' }}>Account information:</strong> When you create an account we collect your
                full name, work email address, company name, and the plan you choose.</P>
              <P><strong style={{ color: 'inherit' }}>Billing information:</strong> Payments are processed by Stripe. We store
                only the last four digits of your card, expiry date, billing name, and Stripe customer and subscription
                IDs. We never store full card numbers.</P>
              <P><strong style={{ color: 'inherit' }}>Cloud credential metadata:</strong> When you connect a cloud account
                (AWS, GCP, Azure), we store encrypted access credentials (AES-256-GCM) and the results of security
                scans. We do not store your actual cloud data.</P>
              <P><strong style={{ color: 'inherit' }}>Usage data:</strong> We collect information about how you use the Service,
                including pages visited, features accessed, and actions taken.</P>
              <P><strong style={{ color: 'inherit' }}>Technical data:</strong> IP addresses, browser type, operating system,
                device identifiers, and log data are collected automatically when you access the Service.</P>
              <P><strong style={{ color: 'inherit' }}>Communications:</strong> If you contact us by email or through our contact
                form, we keep a record of that correspondence.</P>
            </Section>

            <Section id="how-we-use" title="3. How We Use Your Information">
              <P>We use the information we collect to:</P>
              <UL items={[
                'Provide, operate, and improve the Shield Service',
                'Create and manage your account and subscription',
                'Process payments via Stripe',
                'Send transactional emails (scan results, security alerts, invoices)',
                'Send product updates and marketing communications (you can opt out at any time)',
                'Detect and prevent fraud, abuse, and security incidents',
                'Comply with legal obligations and enforce our Terms of Service',
                'Generate anonymised, aggregated analytics to understand usage patterns',
              ]} />
            </Section>

            <Section id="legal-basis" title="4. Legal Basis for Processing">
              <P>Where the GDPR applies, we rely on the following legal bases:</P>
              <UL items={[
                'Contract performance — to deliver the Service you have subscribed to',
                'Legitimate interests — to improve the Service, prevent fraud, and ensure security',
                'Legal obligation — to comply with applicable laws and regulations',
                'Consent — for optional marketing communications (you may withdraw consent at any time)',
              ]} />
            </Section>

            <Section id="data-sharing" title="5. Data Sharing and Disclosure">
              <P>We do not sell your personal data. We share data with third parties only in the following circumstances:</P>
              <UL items={[
                'Stripe (payment processing)',
                'Amazon Web Services (infrastructure hosting — [DATA REGION])',
                'Sendgrid / [SMTP PROVIDER] (transactional email delivery)',
                'Analytics providers (anonymised data only)',
                'Law enforcement or regulators when required by applicable law',
                'Successor entities in the event of a merger, acquisition, or sale of assets',
              ]} />
              <P>All third-party processors are bound by data processing agreements and are prohibited from using your
                data for their own purposes.</P>
            </Section>

            <Section id="data-transfers" title="6. International Data Transfers">
              <P>
                Your data is stored in [DATA REGION]. Where we transfer data outside the European Economic Area (EEA),
                we rely on the EU Standard Contractual Clauses (SCCs) or other appropriate safeguards to protect your
                data. For more information, contact privacy@[CONTACT EMAIL].
              </P>
            </Section>

            <Section id="data-retention" title="7. Data Retention">
              <P>We retain your personal data for as long as your account is active and for [RETENTION PERIOD] after
                account closure, unless a longer period is required by law. Scan results and security data are retained
                for [SCAN RETENTION PERIOD] by default and can be deleted from your account settings.</P>
              <P>When your trial expires, your data is kept for 30 days to allow reactivation. After that period,
                account data is permanently deleted unless you have converted to a paid plan.</P>
            </Section>

            <Section id="your-rights" title="8. Your Rights">
              <P>Depending on your location, you may have the following rights regarding your personal data:</P>
              <UL items={[
                'Access — request a copy of the personal data we hold about you',
                'Rectification — ask us to correct inaccurate or incomplete data',
                'Erasure — ask us to delete your personal data ("right to be forgotten")',
                'Restriction — ask us to restrict how we process your data',
                'Portability — receive your data in a machine-readable format',
                'Objection — object to processing based on legitimate interests or for direct marketing',
                'Withdraw consent — for any processing based on your consent',
              ]} />
              <P>To exercise any of these rights, email privacy@[CONTACT EMAIL]. We will respond within 30 days.
                You also have the right to lodge a complaint with your local supervisory authority.</P>
            </Section>

            <Section id="cookies" title="9. Cookies and Tracking">
              <P>We use cookies and similar technologies to:</P>
              <UL items={[
                'Maintain your authenticated session (strictly necessary)',
                'Remember your preferences such as billing frequency toggle (functional)',
                'Understand how you navigate the Service (analytics — you can opt out)',
              ]} />
              <P>You can control cookies through your browser settings. Disabling strictly necessary cookies may
                prevent you from using the Service. We do not use third-party advertising cookies.</P>
            </Section>

            <Section id="security" title="10. Security Measures">
              <P>We take the security of your data seriously and implement the following controls:</P>
              <UL items={[
                'AES-256-GCM encryption for all stored cloud credentials',
                'TLS 1.3 encryption for all data in transit',
                'JWT authentication with short-lived access tokens and rotating refresh tokens',
                'SOC2 Type II certified infrastructure',
                'Role-based access controls (RBAC) limiting staff access to the minimum necessary',
                'Regular penetration testing and vulnerability scanning',
                'Audit logging of all administrative actions',
              ]} />
              <P>Despite these measures, no system is completely secure. If you believe your account has been
                compromised, contact security@[CONTACT EMAIL] immediately.</P>
            </Section>

            <Section id="children" title="11. Children's Privacy">
              <P>
                The Service is not directed at children under the age of 16. We do not knowingly collect personal
                data from children. If you believe we have inadvertently collected data from a child, contact us
                at privacy@[CONTACT EMAIL] and we will delete it promptly.
              </P>
            </Section>

            <Section id="third-party" title="12. Third-Party Links">
              <P>
                The Service may contain links to third-party websites. We are not responsible for the privacy
                practices or content of those sites. We encourage you to read their privacy policies before
                providing any personal data.
              </P>
            </Section>

            <Section id="changes" title="13. Changes to This Policy">
              <P>
                We may update this Privacy Policy from time to time. If we make material changes, we will notify
                you by email or by displaying a prominent notice in the Service at least 14 days before the changes
                take effect. Continued use of the Service after that date constitutes acceptance of the updated
                policy.
              </P>
              <P>
                If you have any questions about this policy, contact us at privacy@[CONTACT EMAIL].
              </P>
            </Section>

            <Section id="credential-security" title="14. Credential Security">
              <P>When you connect a cloud provider (AWS, GCP, Azure, or SSH), your credentials are encrypted
                using AES-256-GCM before being written to our database. We store only encrypted ciphertext —
                your actual keys are never stored in plain text. Credentials are decrypted only during an active
                scan and are never logged, shared, or exposed. Shield requires read-only access only and never
                creates, modifies, or deletes anything in your cloud environment.</P>
            </Section>

          </Box>
        </Box>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.disabled">
          © {new Date().getFullYear()} [COMPANY NAME] ·{' '}
          <Link href="/terms" color="text.disabled" underline="hover">Terms of Service</Link>
          {' · '}
          <Link href="/contact" color="text.disabled" underline="hover">Contact</Link>
        </Typography>
      </Box>
    </Box>
  );
}
